// The PUBLIC_LEDGER team's card when the public ledger keeps refusing nodeStream's top-up of a company's operating account
// for a card change's public ledger fee (#25 phase 6, round 4; round 15: its record's; round 12: raised once it is still failing an hour
// after the first try, never for a platform account that can't afford it), or (round 11) for a change of automatic payment
// amounts' fee, before its signatures are asked for or at its last signature: what autopay/seed-fee wrote on the job. Pure,
// so the unit tests import it.
import { formatDate } from '../../../../helpers/format.js';
import { formatCredits } from '../../../../helpers/public-ledger-credits.js';

type Metadata = ReadonlyArray<{ key?: string | null; value?: string | null } | null | undefined> | null | undefined;

// The last entry of a key, as the engine reads it; null when absent or blank
const text = (metadata: Metadata, key: string): string | null => {
  const value = [...(metadata ?? [])].reverse().find((m) => m?.key === key)?.value;
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
};

const credits = (value: string | null): string | null =>
  value !== null && /^\d+$/.test(value) ? `${formatCredits(Number(value))} credits` : null;

// The card's intro, with the company's name (approved, round 12 follow-ups settled 2026-10-30)
export function cardChangeFeeIntro(companyName: string): string {
  return `${companyName}'s change of automatic payment card is waiting because the public ledger has refused nodeStream's top-up of its operating account for the public ledger fee for over an hour.`;
}

// Round 11: the intro for a change of amounts' fee, mirroring the card change's (approved, round 12 follow-ups)
export function changeFeeIntro(companyName: string): string {
  return `${companyName}'s change of automatic payment amounts is waiting because the public ledger has refused nodeStream's top-up of its operating account for the public ledger fee for over an hour.`;
}

// What the fee is for: a card change (round 15: its record, before its signatures or at its last one), or a change of
// amounts' fee before its signatures (CHANGE) or at its last one (SIGN); a card with no FEE_KIND is a card change's
export type FeeKind = 'CARD' | 'CHANGE' | 'SIGN';

// What the card asks staff to do (approved, round 12 follow-ups)
export const CARD_CHANGE_FEE_TEXT =
  'The top-up is tried again every 5 minutes. Check the error and sort out its cause, then mark this resolved: the top-up is tried again at once.';

export interface CardChangeFee {
  companyName: string;
  kind: FeeKind;
  // What the company's operating account lacks, in whole credits, as "1,200 credits"
  amountNeeded: string | null;
  // The platform account's balance, null when it couldn't be read
  platformBalance: string | null;
  error: string | null;
  // When the fee was first tried, as a long date and time
  firstTried: string | null;
}

export function cardChangeFee(metadata: Metadata): CardChangeFee {
  const first = text(metadata, 'FIRST_TRIED_AT');
  const kind = text(metadata, 'FEE_KIND');
  return {
    companyName: text(metadata, 'SUBJECT_COMPANY_NAME') ?? text(metadata, 'SUBJECT_COMPANY_ID') ?? 'The company',
    kind: kind === 'CHANGE' || kind === 'SIGN' ? kind : 'CARD',
    amountNeeded: credits(text(metadata, 'AMOUNT_NEEDED_CREDITS')),
    platformBalance: credits(text(metadata, 'PLATFORM_BALANCE_CREDITS')),
    error: text(metadata, 'ERROR'),
    firstTried: (first ? formatDate(first, 'dateTime') : '') || null,
  };
}

// The card's intro for what the fee is for
export function feeCardIntro(fee: Pick<CardChangeFee, 'kind' | 'companyName'>): string {
  return fee.kind === 'CARD' ? cardChangeFeeIntro(fee.companyName) : changeFeeIntro(fee.companyName);
}

// The card's rows (approved labels, round 3 follow-ups)
export function cardChangeFeeRows(fee: CardChangeFee): { label: string; value: string | undefined }[] {
  return [
    { label: 'Company', value: fee.companyName },
    { label: 'Amount needed', value: fee.amountNeeded ?? undefined },
    // Approved (round 4 follow-ups, small string 6)
    { label: 'Platform account balance', value: fee.platformBalance ?? "Can't be read right now" },
    { label: 'Error', value: fee.error ?? undefined },
    { label: 'First tried', value: fee.firstTried ?? undefined },
  ];
}
